import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Loader from '../components/Loader/Loader';
import MessageCard from '../components/MessageCard';
import {
  CALL_STATE_ENDED,
  CALL_STATE_JOINED,
  CALL_STATE_JOINING,
  CALL_STATE_LOBBY,
  CALL_STATE_NOT_FOUND,
  CALL_STATE_NOT_BEFORE,
  CALL_STATE_READY,
  CALL_STATE_REDIRECTING,
  CALL_STATE_NOT_ALLOWED,
  CALL_STATE_EXPIRED,
  CALL_STATE_CONNECTION_ERROR,
} from '../contexts/useCallMachine';
// import { useRouter } from 'next/router';
import { HairCheck } from '../components/HairCheck/HairCheck';
import { Box, Button, Stack } from '@mui/material';
import { useUIState } from '../contexts/UIStateProvider';
import { useCallState } from '../contexts/CallProvider';
import { CallAppEffects } from '../../hocs/events';
import { CallAppEvents } from '../../CallApp/hocs/events';
import { CallSelectors } from '../../../../store/call/selectors';
import { getDispatch, getState } from '../../../../store';
import { Navigator } from '../../../../effects/navigator';
import { theme } from '../../../../../common/styles/theme';

export const useCallUI = ({
  state,
  room,
  haircheck,
  redirectUrl,
  callEnded,
  notFoundRedirect = 'not-found',
}) => {
  // const router = useRouter();

  const { setShowAside, closeModal } = useUIState();
  const { saveLevel } = useCallState();
  const prevStateRef = React.useRef(null);
  const roomIdRef = React.useRef(null);
  const currentRoomId = useSelector(CallSelectors.getRoomId);

  useEffect(() => {
    if (currentRoomId) {
      roomIdRef.current = currentRoomId;
    }
  }, [currentRoomId]);

  useEffect(() => {
    const prevState = prevStateRef.current;
    prevStateRef.current = state;
    if (!prevState) return; // skip initial mount
    console.log(`%c🔀 App state changed: ${state}`, `color: gray;`);
    if (state === CALL_STATE_ENDED) {
      setShowAside(null);
      closeModal(); // close any open modals (settings, link) when the call ends externally
      // Nothing was recorded (or ever will be) at this level — no pipeline ran, so there's nothing
      // to poll for; the "being processed" placeholder would otherwise sit there forever.
      if (roomIdRef.current && saveLevel !== 'nothing') {
        // awaitNewRun: a reopened call still reads COMPLETED/DONE from its previous build until this
        // Finish's /process registers the new run — that must not end the poll on its first tick.
        CallAppEvents.pollAudioReadyByRoom(roomIdRef.current, undefined, undefined, false, true);
      }
      getDispatch()(CallAppEffects.onCallEnded(saveLevel === 'nothing'));
      // Clear roomId from the URL (an ejected guest reaches ENDED without leave()).
      Navigator.openCall();
    }
  }, [state]);

  const newCall = () => {
    getDispatch()(CallAppEffects.onLeave());
    Navigator.openCall();
  }

  const renderByState = useCallback(() => {
    // Show loader when state is undefined or ready to join
    if (!state || [CALL_STATE_READY, CALL_STATE_JOINING].includes(state)) {
      return <Loader />;
    }

    // Update the UI based on the state of our call
    switch (state) {
      case CALL_STATE_NOT_FOUND:
        // router.replace(notFoundRedirect);
        newCall();
        location.reload();
        return <MessageCard hideBack >
            <Stack
              gap={1}
              alignItems={'center'}
            >
              <Box>
                Room not found.
              </Box>
              <Box>
                Reloading...
              </Box>
            </Stack>
          </MessageCard>;
      case CALL_STATE_NOT_ALLOWED:
        return (
          <MessageCard error header="Access denied">
            You are not allowed to join this meeting. Please make sure you have
            a valid meeting token.
          </MessageCard>
        );
      case CALL_STATE_NOT_BEFORE:
        return (
          <MessageCard error header="Cannot join before owner">
            This room has `nbf` set, meaning you cannot join the call before the
            owner
          </MessageCard>
        );
      case CALL_STATE_EXPIRED:
        return (
          <MessageCard hideBack >
            <Stack
              gap={2}
              alignItems={'center'}
            >
              <Stack
                gap={1}
                alignItems={'center'}
              >
                <Box
                  sx={{
                    textAlign: 'center',
                    fontSize: theme.typography.h5,
                  }}
                >
                  Room expired
                </Box>
                <Box
                  sx={{
                    textAlign: 'center',
                    fontSize: theme.typography.subtitle2,
                  }}
                >
                  The room you are trying to join has expired. Please create or join
            another room.
                </Box>
              </Stack>
              <Button
                variant={'contained'}
                onClick={()=> {
                  newCall();
                  location.reload();
                }}
              >
                New call
              </Button>
            </Stack>
          </MessageCard>
        );
      case CALL_STATE_CONNECTION_ERROR:
        return (
          <MessageCard error header="Connection problem">
            <Stack gap={2} alignItems={'center'}>
              <Box sx={{ textAlign: 'center' }}>
                Couldn't load the call.
                This usually happens on a slow or restricted network.
                Check your connection and try again.
              </Box>
              <Button variant={'contained'} onClick={() => location.reload()}>
                Try again
              </Button>
            </Stack>
          </MessageCard>
        );
      case CALL_STATE_LOBBY:
        return haircheck ? haircheck() : <HairCheck />;
      case CALL_STATE_JOINED:
        return room ? (
          room
        ) : (
          <MessageCard error header="No room component declared" />
        );
      case CALL_STATE_REDIRECTING:
        if (!redirectUrl) {
          break;
        }
        window.location = redirectUrl;
        break;
      case CALL_STATE_ENDED:
        // the home page takes over right away (CallAppEffects.onCallEnded) and shows the note there
        return callEnded ? callEnded() : <Loader />;
      default:
        break;
    }

    return (
      <MessageCard error header="Something went wrong">
        <Stack gap={2} alignItems={'center'}>
          <Box sx={{ textAlign: 'center' }}>
            A problem occurred in the call.
            Please check your connection and try again.
            If it keeps happening, contact support.
          </Box>
          <Button variant={'contained'} onClick={() => location.reload()}>
            Try again
          </Button>
        </Stack>
      </MessageCard>
    );
  }, [
    state,
    notFoundRedirect,
    redirectUrl,
    haircheck,
    room,
    callEnded,
  ]);

  return renderByState;
};

export default useCallUI;
